import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { useAuth } from "../../context/AuthContext";
import { AdminPageHeader, DataTable } from "../../components/admin/DataTable";
import { Button } from "../../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminBenutzer() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    // #136 Befund 4/5: Rechte-Eingriffe (Rolle, Aktiv) fragen nach; prompt()
    // ist durch echte Dialoge ersetzt.
    const [confirmAction, setConfirmAction] = useState(null); // {kind, user}

    const load = () => rpc.get("/admin/users").then((r) => r.ok && setUsers(r.data));
    useEffect(() => { load(); }, []);

    const runConfirmed = async () => {
        const { kind, user: u } = confirmAction;
        setConfirmAction(null);
        if (kind === "role") {
            const nextRole = u.role === "admin" ? "user" : "admin";
            const res = await rpc.post(`/admin/users/${u.id}/role`, { role: nextRole });
            if (!res.ok) return toast.error(res.error);
            toast.success(`Rolle -> ${nextRole}`);
        } else {
            const res = await rpc.post(`/admin/users/${u.id}/active`, { is_active: !u.is_active });
            if (!res.ok) return toast.error(res.error);
            toast.success(u.is_active ? "Deaktiviert" : "Aktiviert");
        }
        load();
    };

    const columns = [
        { key: "name", label: "Name", render: (r) => r.name || "—" },
        { key: "email", label: "Email" },
        { key: "role", label: "Rolle" },
        {
            key: "is_active",
            label: "Aktiv",
            render: (r) => (r.is_active ? "ja" : "nein"),
        },
        {
            key: "actions",
            label: "Aktionen",
            sortable: false,
            render: (r) => (
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setConfirmAction({ kind: "role", user: r })} data-testid={`bnz-role-${r.id}`}>
                        Rolle: {r.role === "admin" ? "→ user" : "→ admin"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setConfirmAction({ kind: "active", user: r })} data-testid={`bnz-active-${r.id}`}>
                        {r.is_active ? "Deaktivieren" : "Aktivieren"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRenameTarget(r)} data-testid={`bnz-name-${r.id}`}>
                        Name
                    </Button>
                    {r.id !== me?.id ? (
                        <Button variant="ghost" size="sm" onClick={() => setResetTarget(r)} data-testid={`bnz-pw-${r.id}`}>
                            Passwort
                        </Button>
                    ) : null}
                </div>
            ),
        },
    ];

    return (
        <>
            <AdminPageHeader title="Benutzer" subtitle={`${users.length} Konto(en)`}>
                <Button onClick={() => setShowCreate(true)} data-testid="bnz-new">
                    <Plus className="mr-1 h-4 w-4" /> Neuer Benutzer
                </Button>
            </AdminPageHeader>
            <DataTable
                testId="admin-benutzer-table"
                columns={columns}
                rows={users}
                rowKey={(r) => r.id}
                search={(r) => `${r.email} ${r.name || ""}`}
            />
            <CreateUserDialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={() => { setShowCreate(false); load(); }}
            />
            <ResetPasswordDialog
                target={resetTarget}
                onClose={() => setResetTarget(null)}
            />
            <RenameUserDialog
                target={renameTarget}
                onClose={() => setRenameTarget(null)}
                onRenamed={() => { setRenameTarget(null); load(); }}
            />
            <AlertDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
                <AlertDialogContent data-testid="bnz-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmAction?.kind === "role"
                                ? `Rolle auf ${confirmAction.user.role === "admin" ? "user" : "admin"} ändern?`
                                : confirmAction?.user.is_active
                                    ? "Konto deaktivieren?"
                                    : "Konto aktivieren?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmAction?.kind === "role"
                                ? `${confirmAction.user.email} erhält damit ${
                                    confirmAction.user.role === "admin"
                                        ? "keine Admin-Rechte mehr."
                                        : "volle Admin-Rechte (Stammdaten, Benutzer, Inventur)."
                                }`
                                : confirmAction?.user.is_active
                                    ? `${confirmAction?.user.email} kann sich danach nicht mehr anmelden; laufende Sitzungen enden.`
                                    : `${confirmAction?.user.email} kann sich danach wieder anmelden.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bnz-confirm-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction data-testid="bnz-confirm-submit" onClick={runConfirmed}>
                            Bestätigen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function RenameUserDialog({ target, onClose, onRenamed }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setName(target?.name || ""); }, [target]);
    const submit = async (event) => {
        event.preventDefault();
        setBusy(true);
        const res = await rpc.post(`/admin/users/${target.id}/name`, { name });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Name aktualisiert.");
        onRenamed();
    };
    return (
        <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="bnz-rename-dialog">
                <DialogHeader><DialogTitle>Name ändern</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <Label htmlFor="bnz-rename-input">Name für {target?.email}</Label>
                    <Input
                        id="bnz-rename-input"
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="bnz-rename-input"
                    />
                    <Button type="submit" disabled={busy} data-testid="bnz-rename-submit">
                        {busy ? "..." : "Speichern"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ResetPasswordDialog({ target, onClose }) {
    const [password, setPassword] = useState("");
    const [fieldError, setFieldError] = useState("");
    const [busy, setBusy] = useState(false);
    const close = () => { setPassword(""); setFieldError(""); onClose(); };
    const submit = async (event) => {
        event.preventDefault();
        if (password.length < 8) return setFieldError("Mindestens 8 Zeichen.");
        setFieldError("");
        setBusy(true);
        const res = await rpc.post(`/admin/users/${target.id}/password`, { password });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Passwort gesetzt. Der Benutzer wird überall abgemeldet.");
        close();
    };
    return (
        <Dialog open={!!target} onOpenChange={(v) => !v && close()}>
            <DialogContent data-testid="bnz-pw-dialog">
                <DialogHeader><DialogTitle>Passwort zurücksetzen</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <p className="text-sm text-muted-foreground">
                        Neues Passwort für <span className="font-medium text-foreground">{target?.email}</span>.
                        Alle Sitzungen dieses Benutzers werden beendet. Das Passwort bitte
                        persönlich oder über einen sicheren Kanal übergeben.
                    </p>
                    <Label htmlFor="bnz-pw-input">Neues Passwort (mind. 8 Zeichen) *</Label>
                    <Input
                        id="bnz-pw-input"
                        type="password"
                        autoFocus
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        aria-invalid={!!fieldError}
                        data-testid="bnz-pw-input"
                    />
                    {fieldError ? (
                        <p className="text-xs text-destructive" data-testid="bnz-pw-error">{fieldError}</p>
                    ) : null}
                    <Button type="submit" disabled={busy} data-testid="bnz-pw-submit">
                        {busy ? "..." : "Passwort setzen"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CreateUserDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const submit = async (event) => {
        event.preventDefault();
        const required = "Pflichtfeld.";
        const next = {};
        if (!form.email) next.email = required;
        if (!form.password) next.password = required;
        setErrors(next);
        if (Object.keys(next).length > 0) return;
        setBusy(true);
        const res = await rpc.post("/admin/users", form);
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Benutzer angelegt.");
        setForm({ email: "", password: "", name: "", role: "user" });
        onCreated();
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="bnz-create-dialog">
                <DialogHeader><DialogTitle>Neuer Benutzer</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <Label htmlFor="bnz-new-email">Email *</Label>
                    <Input id="bnz-new-email" autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-invalid={!!errors.email} data-testid="bnz-new-email" />
                    {errors.email ? <p className="text-xs text-destructive" data-testid="bnz-new-email-error">{errors.email}</p> : null}
                    <Label htmlFor="bnz-new-pw">Passwort *</Label>
                    <Input id="bnz-new-pw" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} aria-invalid={!!errors.password} data-testid="bnz-new-pw" />
                    {errors.password ? <p className="text-xs text-destructive" data-testid="bnz-new-pw-error">{errors.password}</p> : null}
                    <Label htmlFor="bnz-new-name">Name</Label>
                    <Input id="bnz-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <Label>Rolle</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="user">user</SelectItem>
                            <SelectItem value="admin">admin</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button type="submit" disabled={busy} data-testid="bnz-new-submit">
                        {busy ? "..." : "Anlegen"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
